import React, {useEffect, useRef, useState, useMemo} from 'react';
import {Link, useParams} from "react-router-dom";

import {ScrollPanel} from 'primereact/scrollpanel';
import {CourseService} from "../../service/CourseService";
import {ChapterService} from "../../service/ChapterService";
import CourseContent from "../../components/courseContent/CourseContent";
import {TabPanel, TabView} from 'primereact/tabview';
import {Dialog} from 'primereact/dialog';
import {Card} from "primereact/card";
import {RadioButton} from 'primereact/radiobutton';
import List from "../../components/List";
import {CompletedLectureService} from "../../service/CompletedLectureService";
import GeneralUtils from "../../utils/GeneralUtils";
import {Button} from "primereact/button";
import Note from "./note/Note";
import {Divider} from "primereact/divider";
import {Panel} from "primereact/panel";
import {Rating} from "primereact/rating";
import VideoPlayer from "../../components/videoPlayer/VideoPlayer";
import Transcript from "../../components/videoPlayer/Transcript";
import InstructorInfo from "../Instructor/InstructorInfo";
import "./CourseView.css";
import {useBrowserLogger} from "../../hooks/logger/useBrowserLogger";
import {useMouseLoggerWithBuffer} from "../../hooks/logger/useMouseLoggerWithBuffer";
// import {useInactivityTracker} from "../../components/tracking/inactivity/useInactivityTracker";
// import {logEventBus} from "../../components/tracking/inactivity/logEventBus";

import {useActivityLogger} from "../../hooks/logger/activity/useActivityLogger";
import ExamContainer from "../Exam/ExamContainer";
import {useSnapshotLogger} from "../../hooks/logger/useSnapshotLogger";
import {useMouseLogger} from "../../hooks/logger/useMouseLogger";
import {SubscribedCourseService} from "../../service/SubscribedCourseService";
import {CaptionService} from "../../service/CaptionService";
// import {useExitLogger} from "../../hooks/logger/useExitLogger";

const logoIcon = "/assets/images/culogo_beyaz.svg";

const CourseView = () => {
    const [data, setData] = useState([]);
    const [chapter, setChapter] = useState([]);
    const [completedLectureData, setCompletedLectureData] = useState([]);
    const [displaySidebar, setDisplaySidebar] = useState(true);
    const [videoURL, setVideoURL] = useState(null);
    const [completedLecture, setCompletedLecture] = useState(null);
    const [activeChapter, setActiveChapter] = useState(null);
    const [activeLecture, setActiveLecture] = useState(null);
    const [activeExam, setActiveExam] = useState(false);
    const [captions, setCaptions] = useState([]);
    const [sidebarTabIndex, setSidebarTabIndex] = useState(0); // 0: Kurs İçeriği, 1: Transkript
    const [activeCaptionIndex, setActiveCaptionIndex] = useState(null); // VideoPlayer'dan callback ile güncellenir
    const [courseContentAccordionIndex, setCourseContentAccordionIndex] = useState(null); // CourseContent accordion state'ini koru
    const [showTranscript, setShowTranscript] = useState(false); // Transkript tab'ını göster/gizle
    const [fullscreenContainer, setFullscreenContainer] = useState(null); // Video fullscreen container

    const [currentTime, setCurrentTime] = useState(null);
    const [currentTimeForNote, setCurrentTimeForNote] = useState(null);
    const [displayNoteEditor, setDisplayNoteEditor] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const [displayRatingDialog, setDisplayRatingDialog] = useState(false);
    const [rating, setRating] = useState(null);

    // const [showInactivityModal, setShowInactivityModal] = useState(false);

    // Create services once
    const service = useMemo(() => new CourseService(), []);
    const chapterService = useMemo(() => new ChapterService(), []);
    const completedLectureService = useMemo(() => new CompletedLectureService(), []);
    const subscribedCourseService = useMemo(() => new SubscribedCourseService(), []);
    const captionService = useMemo(() => new CaptionService(), []);

    const [isPlaying, setIsPlaying] = useState(false);
    const playerRef = useRef();
    const duration = playerRef.current?.getDuration?.();

    const {id} = useParams();

    useBrowserLogger(activeLecture?.asset?.id);
    useMouseLogger(activeLecture?.asset?.id);
    useSnapshotLogger(activeLecture?.asset?.id, {
        enabled: true,
        enablePeriodicCapture: true,
        interval: 30000,
        scrollThrottle: 2000,
        enableScrollCapture: true,
        observeTargetSelector: ".cvp-container-wrapper",
        playbackActiveOverride: () => isPlaying,
        skipInitialWhenMedia: false,
        debug: false, // Turn debug back on to diagnose
    });

    const { popupVisible, handleContinue } = useActivityLogger({
            assetId: activeLecture?.asset?.id,
        currentTime,
        isPlaying,
        duration,
        onContinue: () => {
            playerRef.current?.handlePlayPause();
        },
        onPopupShow: () => {
            playerRef.current?.handlePlayPause();
        }
    });

    // // Exit logger (erken çıkış anketi)
    // const {
    //     exitSurveyVisible,
    //     reasons: exitReasons,
    //     reason: exitReason,
    //     setReason: setExitReason,
    //     exitRating,
    //     setExitRating,
    //     submitExitSurvey,
    //     dismissExitSurvey,
    //     forceExit
    // } = useExitLogger({
    //     assetId: activeLecture?.asset?.id,
    //     currentTime: currentTime || 0,
    //     duration: duration || 0,
    //     options: { enable: !!activeLecture?.asset?.id, minProgressPercent: 90 }
    // });


    useEffect(() => {
        service.findById(id).then(response => setData(response));
        chapterService.findByCourseId(id).then(response => setChapter(response));
        completedLectureService.findByCourseId(id).then(response => setCompletedLectureData(response || []));
        subscribedCourseService.updateLastAccessedTime(id);
    }, [id, service, chapterService, completedLectureService]);

    // Fetch captions when active lecture changes
    useEffect(() => {
        if (activeLecture?.asset?.id) {
            captionService.findByAssetId(activeLecture.asset.id)
                .then(response => setCaptions(response || []))
                .catch(() => setCaptions([]));
        } else {
            setCaptions([]);
        }
    }, [activeLecture?.asset?.id, captionService]);

    const handleStart = (chapter, lecture) => {
        setCurrentTime(0);
        setVideoURL(lecture.asset.videoUrl ? lecture.asset.videoUrl : null);
        setActiveChapter(chapter);
        setActiveLecture(lecture);
        setActiveExam(null);
        let data = {startedTime: new Date(), course: id, chapter:chapter, lecture: lecture};
        completedLectureService.create(data).then(() => {
            setCompletedLecture(data);
            completedLectureService.findByCourseId(id).then(response => setCompletedLectureData(response || []));
            }
        );
    }

    const handleStartExam = (chapter, exam) => {
        setActiveChapter(chapter);
        setActiveExam(exam);
        setActiveLecture(null);
        // setCompletedLecture({startedTime: new Date(), course: id, chapter:chapter, lecture: lecture});
    }

    const handleEnded = () => {
        if(!GeneralUtils.isNullOrEmpty(completedLecture)) {
            completedLecture.completedTime = new Date();
            completedLectureService.update(completedLecture).then(() =>{
                setCompletedLecture(data);
                completedLectureService.findByCourseId(id).then(response => setCompletedLectureData(response || []));
            }
            );
        }
    }


    const addNote = () => {
        setDisplayNoteEditor(true);
        setCurrentTimeForNote(currentTime);
        setActiveIndex(1);
    }

    const handleTranscriptToggle = () => {
        if (showTranscript && displaySidebar) {
            // Transkript açık VE sidebar görünüyorsa → Transkript'i kapat
            setShowTranscript(false);
            setSidebarTabIndex(0);
        } else {
            // Transkript kapalı VEYA sidebar kapalıysa → Her ikisini de aç
            setDisplaySidebar(true); // Sidebar'ı aç (fullWidth false olur)
            setShowTranscript(true);
            setSidebarTabIndex(1);
        }
    }

    const handleSeekToTime = (time) => {
        if (playerRef.current) {
            playerRef.current.seekTo(time);
        }
    }

    const formatDuration = (seconds) => {
        return new Date(seconds * 1000).toISOString().substr(11, 8);
    };



    const header = (
        <div className="cvp-header">
            <div className="cvp-header-left">
                <Link to="/">
                    <img src={logoIcon} alt="logo" style={{height: "32px"}}/>
                </Link>
                <hr className="vertical-hr"/>
                <h5 style={{color: 'var(--primary-color-text)', margin: '0'}}>{data.title}</h5>
            </div>
            <div className="cvp-header-right">
                {/*<Button onClick={() => setDisplayRatingDialog(true)}*/}
                {/*        icon="pi pi-star-fill" label="Puan verin" text size="small" style={{color: 'var(--primary-color)'}} />*/}
                <Button icon="pi pi-circle" label="İlerleme durumunuz" tooltip={completedLectureData.length + "/" + data.numOfPublishedLectures + " tamamlandı"}
                        tooltipOptions={{ position: 'bottom'}}
                        text size="small" style={{color: 'var(--primary-color-text)'}}/>

                {/* optional controls removed for now */}

                <Button  onClick={() => setDisplaySidebar((prev) => !prev)}
                         icon="pi pi-bars" text size="small" style={{color: 'var(--primary-color-text)'}} className="button-black"/>
            </div>
        </div>
    )



    const overview = (
    <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        <TabPanel header="Genel bakış">
            <Card title='Öğrenecekleriniz' className='card noshadow'>
                <List data={data.objectives}/>
            </Card>

            <Card title='Gereksinimler' className='noshadow noborder'>
                <List data={data.prerequisites}/>
            </Card>
            <Divider/>

            <Card title='Açıklama' className='noshadow noborder'>
                <div dangerouslySetInnerHTML={{__html: data.description}}/>
            </Card>
            <Divider/>

            <Card title='Bu kurs kimler için uygun:' className='noshadow noborder'>
                <List data={data.targetAudiences}/>
            </Card>
            <Divider/>

            {data.visibleInstructorList?.map((instructor) =>
                <>
                    <InstructorInfo data={instructor}/>
                    <Divider/>
                </>
            )}
        </TabPanel>
        <TabPanel leftIcon="pi pi-file-edit mr-2" header="Notlar">
            {!displayNoteEditor && <Button onClick={() => {setDisplayNoteEditor(true);setCurrentTimeForNote(currentTime)}}
                                           label={formatDuration(currentTime) + " konumunda yeni not oluştur "} size="small" outlined/>}
            <Note course={data} chapter={activeChapter} lecture={activeLecture} currentTime={currentTimeForNote} displayNoteEditor={displayNoteEditor} setDisplayNoteEditor={setDisplayNoteEditor}/>
        </TabPanel>
        {/*<TabPanel header="Soru & Cevap"></TabPanel>*/}
        {/*<TabPanel header="Duyurular"></TabPanel>*/}
        {/*<TabPanel header="Yorumlar"></TabPanel>*/}
        {/*<TabPanel leftIcon="pi pi-search mr-2"></TabPanel>*/}
    </TabView>
    )

    const ratingDialog = (
        <Dialog header="Bu kursu nasıl değerlendirirsiniz?" visible={displayRatingDialog} style={{width: '35vw'}} onHide={() => setDisplayRatingDialog(false)}>
                <Rating value={rating} onChange={(e) => setRating(e.value)} />
        </Dialog>
    )


    const getPopup = (
        <Dialog
            header="Onay"
            visible={popupVisible}
            style={{width: '25vw'}}
            closable={false}
            onHide={() => {}}
            appendTo={fullscreenContainer || document.body}
            modal={true}
        >
            <i className="pi pi pi-exclamation-triangle"
               style={{color: 'var(--primary-color)', fontSize: '1.25rem'}}>
                Uzun süredir etkileşimde
                bulunmadınız. Devam etmek
                istiyor musunuz?
            </i>
            <Button label="Devam Et" onClick={handleContinue} size="sm" className="mt-4"
                    style={{width: "100%"}}/>
        </Dialog>
    )

    // const exitSurveyDialog = (
    //     <Dialog header="Videoyu ne kadar verimle izlediniz?" visible={exitSurveyVisible} style={{ width: '30vw' }} closable={false} onHide={() => {}}>
    //         <div className="flex flex-column gap-3">
    //             <Rating value={exitRating} onChange={(e) => setExitRating(e.value)} cancel={false}/>
    //
    //             <div style={{fontWeight: 600, marginTop: '2rem'}}>Dersi neden yarıda bırakıyorsunuz? Bir neden seçin:</div>
    //             {exitReasons.map(r => (
    //                 <div key={r} className="flex align-items-center gap-2"
    //                      style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
    //                     <RadioButton inputId={`exit-reason-${r}`} name="exitReason" value={r}
    //                                  onChange={(e) => setExitReason(e.value)} checked={exitReason === r}/>
    //                     <label htmlFor={`exit-reason-${r}`}>{r}</label>
    //                 </div>
    //             ))}
    //             <div className="flex justify-content-end gap-2 mt-4"
    //                  style={{display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem'}}>
    //                 <Button label="Vazgeç" severity="secondary" text onClick={dismissExitSurvey}/>
    //                 <Button label="Gönder" disabled={!exitReason} onClick={submitExitSurvey}/>
    //                 <Button label="Çık" severity="danger" onClick={forceExit}/>
    //             </div>
    //         </div>
    //     </Dialog>
    // )




    return (
        <div id="cvp-container-wrapper" className="cvp-container-wrapper" style={{backgroundColor: "var(--gray)"}}>
            {header}
            <div className="cvp-container">
                <div className="cvp-left" style={{width: !displaySidebar ? "100%" : "75%"}}>
                    <div className="cvp-content" style={{height: !displaySidebar ? "100%" : "75%"}}>
                        {/* Video area */}
                        <div className="video-gallery" data-skip-snapshot="true"
                             style={{height: "720px", display: "flex", justifyContent: "center"}}>
                            {!(activeLecture?.asset || activeExam) &&
                                <img src="/assets/images/culogo_circle_small.png" alt="logo" style={{margin: "auto"}}/>}
                            {activeLecture &&
                                <>
                                {(activeLecture?.asset?.assetType === 'Video' && videoURL) && <VideoPlayer ref={playerRef}
                                                          key={activeLecture?.asset?.id} // critical: force re-render
                                                          assetId={activeLecture?.asset?.id}
                                                          url={videoURL}
                                                          captions={captions}
                                                          onEnded={handleEnded}
                                                          onPlayingChange={setIsPlaying}
                                                          setCurrentTime={setCurrentTime}
                                                          note addNote={addNote}
                                                          fullWidth={!displaySidebar}
                                                          setFullWidth={() => setDisplaySidebar((prev) => !prev)}
                                                          onTranscriptToggle={handleTranscriptToggle}
                                                          onActiveCaptionChange={setActiveCaptionIndex}
                                                          isTranscriptOpen={showTranscript}
                                                          onFullscreenChange={(isFs, container) => setFullscreenContainer(isFs ? container : null)}
                                                          />}
                                {activeLecture?.asset?.assetType === 'Article' &&
                                    <ScrollPanel key={activeLecture?.asset?.id} // critical: force re-render
                                                 style={{
                                                     width: '100%',
                                                     height: '100%',
                                                     backgroundColor: "white",
                                                     color: "black"
                                                 }}>
                                        <div style={{width: '50%', margin: "auto"}}>
                                            <div dangerouslySetInnerHTML={{__html: activeLecture.asset?.body}}/>
                                        </div>
                                    </ScrollPanel>}

                                {activeLecture?.asset?.assetType === 'Presentation' &&
                                    <div key={activeLecture?.asset?.id} data-skip-snapshot="true"
                                         style={{width: '100%', height: '100%', backgroundColor: "white", color: "black"}}>
                                        <iframe src={activeLecture.asset?.downloadUrl} width="100%" height="720"
                                                title={activeLecture.asset?.downloadUrl}
                                                style={{ border: 0 }}
                                                allowFullScreen/>
                                    </div>}
                                </>}

                            {activeExam &&
                                <ExamContainer exam={activeExam}
                                               fullWidth={displaySidebar}
                                               setFullWidth={() => setDisplaySidebar((prev) => !prev)}
                                />
                            }
                        </div>
                    </div>
                    {overview}
                </div>

                {displaySidebar &&
                    <div className="cvp-right">
                        <TabView
                            activeIndex={sidebarTabIndex}
                            onTabChange={(e) => setSidebarTabIndex(e.index)}
                        >
                            <TabPanel header="Kurs İçeriği">
                                <CourseContent
                                    course={data}
                                    data={chapter}
                                    completedData={completedLectureData}
                                    onStart={(chapter, lecture) => handleStart(chapter, lecture)}
                                    onStartExam={(chapter, exam) => handleStartExam(chapter, exam)}
                                    accordionActiveIndex={courseContentAccordionIndex}
                                    onAccordionChange={(e) => setCourseContentAccordionIndex(e.index)}
                                />
                            </TabPanel>

                            {showTranscript && captions.length > 0 && (
                                <TabPanel header="Transkript">
                                    <Transcript
                                        captionUrl={captions[activeCaptionIndex !== null ? activeCaptionIndex : 0]?.url}
                                        currentTime={currentTime}
                                        onSeek={handleSeekToTime}
                                    />
                                </TabPanel>
                            )}
                        </TabView>
                    </div>
                }
            </div>
            {ratingDialog}


            {getPopup}

            {/*{exitSurveyDialog}*/}
        </div>
    )
}

export default CourseView;
